import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  AppBar, 
  Toolbar, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  BusinessCenter as BusinessCenterIcon,
  Psychology as PsychologyIcon,
  Headset as HeadsetIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BetFiBackOffice = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { token } = useAuth();
  const drawerWidth = 240;

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background effects */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />
      
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(30, 35, 45, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            component={Link}
            to="/"
            color="inherit"
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            BetFi BackOffice
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 2, px: 1 }}>
          <List>
            <ListItem button component={Link} to="/">
              <ListItemIcon sx={{ color: 'white' }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button component={Link} to="/bots">
              <ListItemIcon sx={{ color: 'white' }}>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="My Bots" />
            </ListItem>
            <ListItem button component={Link} to="/support">
              <ListItemIcon sx={{ color: 'white' }}>
                <HeadsetIcon />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button selected>
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <BusinessCenterIcon />
              </ListItemIcon>
              <ListItemText primary="BetFi BackOffice" primaryTypographyProps={{ fontWeight: 'medium' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 2, px: 1 }}>
          <List>
            <ListItem button component={Link} to="/" sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button component={Link} to="/bots" sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="My Bots" />
            </ListItem>
            <ListItem button component={Link} to="/support" sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <HeadsetIcon />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button selected sx={{ 
              backgroundColor: 'rgba(76, 133, 240, 0.15) !important',
              borderRight: `3px solid ${theme.palette.primary.main}`
            }}>
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <BusinessCenterIcon />
              </ListItemIcon>
              <ListItemText primary="BetFi BackOffice" primaryTypographyProps={{ fontWeight: 'medium' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 0, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          pt: { xs: 8, sm: 8 },
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        {/* Loading indicator */}
        {loading && (
          <Box 
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backgroundColor: 'rgba(30, 35, 45, 0.9)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
                Loading BetFi BackOffice...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Iframe container */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            border: 'none', 
            width: '100%', 
            height: '100%',
            overflow: 'hidden'
          }}
        >
          <iframe 
            src="https://dev-management.betfi365.com/" 
            title="BetFi BackOffice"
            style={{ 
              width: '100%', 
              height: '100%', 
              border: 'none',
            }}
            onLoad={handleIframeLoad}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </Box>
      </Box>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); }
          100% { transform: translateY(-20px) rotate(10deg); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default BetFiBackOffice;
